import { Injectable } from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {map, Observable} from "rxjs";
import {environment} from "../../environments/environment";
import {Page} from "./api.service";
import {Assessment} from "./volunteer-task.service";
import {AgendaItem} from "./room.service";
import { Activity, ActivityResponse } from './activity.service';

export type Reward = 'COMPLETED' | 'ASSESSED_POOR' | 'ASSESSED_OK' | 'ASSESSED_GOOD' ;
export type RewardMap = { [key in Reward]: number ; }
export const DEFAULT_REWARDS: RewardMap = {
  'COMPLETED': 5,
  'ASSESSED_POOR': -5,
  'ASSESSED_OK': 5,
  'ASSESSED_GOOD': 10
}

export type Status = 'PENDING'|'ATTEMPTING'|'AWAITING_ASSESSMENT'|'COMPLETED' ;

export class ClassTask extends Activity{

  subtasks: Subtask[];

  constructor (forGroups: boolean, subtasks: Subtask[]) {
    super('CLASS_TASK', forGroups);
    this.subtasks = subtasks ;
  }
}

export class ClassTaskAgendaItem extends AgendaItem {

  subtasks: Subtask[] ;
  activityId?: string ;

  constructor(title: string, forGroups: boolean, subtasks: Subtask[]) {
    super('CLASS_TASK', forGroups, title) ;
    this.subtasks = subtasks ;
  }
}

export class Subtask {
  name: string ;
  bonus: boolean ;
  rewards: RewardMap ;

  constructor (name: string, bonus: boolean) {
    this.name = name ;
    this.bonus = bonus ;
    this.rewards = DEFAULT_REWARDS ;
  }
}

export class SubtaskProgress {
  status?: Status ;
  assessment?: Assessment ;

  constructor(status?: Status, assessment?: Assessment) {
    this.status = status ;
    this.assessment = assessment ;
  }
}

export interface ClassTaskResponse extends ActivityResponse {

  status: Status;
  statusChangedAt: Date ;

  subtaskProgress: SubtaskProgress[];
}

@Injectable({
  providedIn: 'root'
})
export class ClassTaskService {

  constructor(
    private http: HttpClient
  ) {
  }

  getCurrent(roomId: string): Observable<ClassTask| undefined> {
    return this.http.get<Activity>(
      `${environment.apiHost}/rooms/${roomId}/activities/current`
    ).pipe(
      map(activity => {

        if (!activity || activity.type != 'CLASS_TASK') 
          return undefined ;
        else 
          return activity as ClassTask ;

      })
    );
  }

  start(roomId: string, task: ClassTask): Observable<ClassTask> {
    return this.http.post(
      `${environment.apiHost}/rooms/${roomId}/activities`,
      task
    ) as Observable<ClassTask>;
  }

  getResponses(taskId: string, page: number ): Observable<Page<ClassTaskResponse>> {

    let params = {
      page: (page) ? page : 0
    }

    return this.http.get<Page<ClassTaskResponse>>(
      `${environment.apiHost}/activities/${taskId}/responses`,
      {params}
    ) ;
  }

  getMyProgress(taskId: string): Observable<ClassTaskResponse> {
    return this.http.get<ClassTaskResponse>(
      `${environment.apiHost}/activities/${taskId}/responses/mine`,
    ) ;
  }

  updateMyProgress(taskId: string, subtasks: (SubtaskProgress|null)[]): Observable<ClassTaskResponse> {

    return this.http.patch<ClassTaskResponse>(
      `${environment.apiHost}/activities/${taskId}/responses/mine`,
      {
        type: 'CLASS_TASK',
        subtaskProgress: subtasks
      }
    ) ;
  }

  updateProgress(taskId: string, userOrGroupId:string, subtasks: (SubtaskProgress|null)[]): Observable<ClassTaskResponse> {

    console.log("Updating task progress", {subtaskProgress: subtasks});

    return this.http.patch<ClassTaskResponse>(
      `${environment.apiHost}/activities/${taskId}/responses/${userOrGroupId}`,
      {
        type: 'CLASS_TASK',
        subtaskProgress: subtasks
      }
    ) ;
  }

  makeAdjustment(taskId: string, userOrGroupId: string, subtaskIndex:number, completed: boolean, assessment: Assessment | null, explanation: string): Observable<ClassTaskResponse> {

    return this.http.post<ClassTaskResponse>(
      `${environment.apiHost}/activities/${taskId}/responses/${userOrGroupId}/adjustments`,
      {
        type: 'CLASS_TASK',
        subtaskIndex,
        completed,
        assessment,
        explanation
      }
    ) ;
  }


  pause(taskId: string): Observable<ClassTask> {
  
    return this.http.patch<ClassTask>(
      `${environment.apiHost}/activities/${taskId}`,
      {
        type: 'CLASS_TASK',
        status: 'PAUSED'
      }
    ) ;
  }

  resume(taskId: string): Observable<ClassTask> {
    return this.http.patch<ClassTask>(
      `${environment.apiHost}/activities/${taskId}`,
      {
        type: 'CLASS_TASK',
        status: 'ACTIVE'
      }
    ) ;
  }

  close(taskId: string): Observable<ClassTask> {
    return this.http.patch<ClassTask>(
      `${environment.apiHost}/activities/${taskId}`,
      {
        type: 'CLASS_TASK',
        status: 'COMPLETED'
      }
    ) ;
  }

  cancel(taskId: string): Observable<ClassTask> {

    return this.http.delete(
      `${environment.apiHost}/activities/${taskId}`,
    ) as Observable<ClassTask>;
  }

  /*
  getKnowledgeEffects(task: ClassTask, response: ClassTaskResponse, attendee: Attendee): PointEffect[] {

    let knowledgePointsOffered = attendee.knowledgePointsOffered ;
    let goodDelta = 0 ;
    let okDelta = 0 ;
    let anyAssessed = false ;

    for (let i=0 ; i<task.subtasks.length ; i++) {

      let s = task.subtasks[i] ;
      let p = response.subtaskProgress[i] ;

      if (p.status == 'COMPLETED') {
        anyAssessed = true ;
        continue ;
      }

      goodDelta += s.rewards['ASSESSED_GOOD'] - s.rewards['ASSESSED_POOR'];
      okDelta += s.rewards['ASSESSED_OK'] - s.rewards['ASSESSED_POOR'];
      if (!s.bonus) {
        knowledgePointsOffered += (s.rewards['ASSESSED_GOOD'] - s.rewards['ASSESSED_POOR']) ;
      }
    }

    const effects: PointEffect[] = [] ;

    if (goodDelta == 0) {
      // all tasks assessed
      return effects;
    }


    const good = (attendee.knowledgePointsEarned + goodDelta) / knowledgePointsOffered ;
    effects.push({
      points: Math.round((good - attendee.knowledgeScore) * 100 ),
      explanation: (anyAssessed) ? "for good feedback on remaining tasks": "for good feedback on all tasks"
    })

    const ok = (attendee.knowledgePointsEarned + okDelta) / knowledgePointsOffered ;
    effects.push({
      points: Math.round((ok - attendee.knowledgeScore) * 100 ),
      explanation: (anyAssessed) ? "for ok feedback on remaining tasks": "for ok feedback on all tasks"
    })

    const poor = (attendee.knowledgePointsEarned + okDelta) / knowledgePointsOffered ;
    effects.push({
      points: Math.round((poor - attendee.knowledgeScore) * 100 ),
      explanation: (anyAssessed) ? "for poor feedback on remaining tasks": "for poor feedback on all tasks"
    })

    return effects ;
  }

  getParticipationEffects(task: ClassTask, response: ClassTaskResponse, attendee: Attendee): PointEffect[] {

    let participationPointsOffered = attendee.participationPointsOffered ;
    let completionDelta = 0 ;
    let anyCompleted = false ;

    for (let i=0 ; i<task.subtasks.length ; i++) {

      let s = task.subtasks[i] ;
      let p = response.subtaskProgress[i] ;

      if (p.status == 'COMPLETED' ||  p.status=='AWAITING_ASSESSMENT') {
        anyCompleted = true ;
        continue ;
      }

      completionDelta += s.rewards['COMPLETED'] ;
      if (!s.bonus) {
        participationPointsOffered += s.rewards['COMPLETED'] ;
      }
    }

    const effects: PointEffect[] = [] ;

    if (completionDelta == 0) {
      // all tasks completed
      return effects;
    }

    const completed = (attendee.participationPointsEarned + completionDelta) / participationPointsOffered ;
    effects.push({
      points: Math.round((completed - attendee.participationScore) * 100 ),
      explanation: (anyCompleted) ? "for completing remaining tasks": "for completing all tasks"
    })

    return effects ;
  }
    */
}
